<template>
	<div class="component-container component-edit-account">
		<Modal v-if="active" @close="closeModal">
			<template #header>
				<p class="modal-title">
					Edit Your Account
				</p>
			</template>

			<template #content>
				<div class="form-container">
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">Current Password *</label>
						<input v-model="user.current_password" type="password" class="form-input" placeholder="Enter your existing password...">
					</div>

					<br>

					<div class="form-field">
						<label class="form-label">Name *</label>
						<input v-model="user.name" type="text" class="form-input" placeholder="Enter your name...">
					</div>
					<div class="form-field">
						<label class="form-label">Email Address *</label>
						<input v-model="user.email" type="email" class="form-input" placeholder="Enter your email address...">
					</div>
					<div class="form-field">
						<label class="form-label">Mobile Number <span v-if="!$store.state.account.isManualLoginDenied && user.preferred2faMethod === 'sms'">*</span></label>
						<input v-model="user.mobileNumber" type="tel" class="form-input" placeholder="Enter your mobile number...">
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">2FA Method *</label>
						<select v-model="user.preferred2faMethod" class="form-input">
							<option value="sms">
								SMS
							</option>
							<option value="email">
								Email
							</option>
							<option value="totp">
								Authenticator app
							</option>
						</select>
					</div>
					<div v-if="user.preferred2faMethod === 'totp' && !$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">
							<help label="user.authenticatior_app">Authenticator App</help>
						</label>
						<template v-if="user.totpConfigured">
							<div class="twofa-section">
								<p><i class="success fas fa-check" /> Setup OK</p>

								<button class="btn btn-create" @click="openModal()">
									Reset 2FA Setup
								</button>
							</div>
						</template>
						<template v-else>
							<div class="twofa-section">
								<p><i class="warning fa-spin fas fa-spinner" /> Awaiting setup</p>

								<button class="btn btn-create" @click="openModal()">
									Start 2FA Setup
								</button>
							</div>
						</template>
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">New Password</label>
						<input v-model="user.new_password" type="password" class="form-input" placeholder="Enter a new password...">
					</div>
					<div v-if="!$store.state.account.isManualLoginDenied" class="form-field">
						<label class="form-label">Confirm New Password</label>
						<input v-model="user.new_password_confirm" type="password" class="form-input" placeholder="Confirm your new password...">
					</div>
				</div>
			</template>

			<template #footer>
				<button class="btn btn-save" title="Update Account" @click="updateAccount">
					Update Account
				</button>
			</template>
		</Modal>
		<Modal v-if="show_2fa_modal" @close="close2faModal">
			<template #header>
				<p class="modal-title">
					Setup 2FA using an Authenticator App
				</p>
			</template>

			<template #content>
				<div class="two-columns">
					<div class="left-column">
						<p class="info">
							You now need to configure two factor authentication, using an authenticator app on your mobile device.
						</p>
						<div class="row">
							<div class="bullet-point">
								1
							</div>
							<div>
								<p class="title">
									Open your authenticator app
								</p>
								<p class="text">
									If you don't have one, they can be downloaded from your app store.
								</p>
							</div>
						</div>
						<div class="row">
							<div class="bullet-point">
								2
							</div>
							<div>
								<p class="title">
									Add a new account
								</p>
								<p class="text">
									Use the <b>+</b> or <b>Add Account</b> button in the app and select the QR code option to scan the code shown on the right.
								</p>
							</div>
						</div>
						<div class="row">
							<div class="bullet-point">
								3
							</div>
							<div>
								<p class="title">
									Enter the 6-digit code from the app below
								</p>
								<input
									v-model="verify_code"
									type="text"
									class="code"
									placeholder="Code..."
									@keydown.enter="verify2faCode"
								>
							</div>
						</div>
					</div>
					<div class="qrcode">
						<img src="/api/authentication/2fa/qr-code.png" alt="QR Code">
					</div>
				</div>
			</template>

			<template #footer>
				<button class="btn btn-danger" title="Cancel" @click="close2faModal">
					Cancel
				</button>
				<button class="btn btn-success" title="Verify Code" @click="verify2faCode">
					Verify
				</button>
			</template>
		</Modal>
	</div>
</template>

<script setup lang="ts">
	import type { GeneralHelper } from '../helper/general';
	import type { HttpHelper } from '../helper/http';
	import Modal from './Modal.vue';
	import { Messages } from '../enums/messages';
	import { validateMobile } from '../helper/phone';
	import { computed, inject, ref } from 'vue';
	import { useStore } from 'vuex';

	defineProps<{
		active: boolean,
	}>();

	const emit = defineEmits<{
		(e: 'change', value: boolean): void,
	}>();

	const store = useStore();
	const http = inject<HttpHelper>('http')!;
	const helper = inject<GeneralHelper>('helper')!;
	const show_2fa_modal = ref(false);
	const verify_code = ref('');
	const user = ref<any>({
		name: '',
		email: '',
		mobileNumber: '',
		preferred2faMethod: '',
		current_password: '',
		new_password: '',
		new_password_confirm: '',
		isTotpAuthenticationEnabled: false,
		totpConfigured: false,
	});

	const userId = computed(() => store.state.account.id);

	const closeModal = () => {
		emit('change', false);
		populateUserObject();
	};

	const openModal = () => {
		show_2fa_modal.value = true;
	};

	const close2faModal = () => {
		show_2fa_modal.value = false;
		verify_code.value = '';
	};

	const verify2faCode = async () => {
		if (verify_code.value.length <= 0) {
			store.dispatch('notification/add', {
				type: 'warning',
				message: 'Code value can\'t be empty',
			});
			return;
		}

		const response = await http.post('/api/authentication/verify_2fa_code', { code: verify_code.value });

		if (response.status === 204) {
			store.dispatch('notification/add', {
				type: 'success',
				message: 'Authenticator App successfully set up',
			});

			show_2fa_modal.value = false;
			verify_code.value = '';
			requestAccess();
		}
	};

	const updateAccount = async () => {
		if (user.value.new_password !== '') {
			const errorMessage: boolean | string = helper.validatePassword(user.value.new_password, user.value.new_password_confirm);
			if (errorMessage !== false) {
				store.dispatch('notification/add', {
					type: 'warning',
					message: String(errorMessage),
				});
				return;
			}
		}

		// Validate existing password.
		if (user.value.current_password === '' && !store.state.account.isManualLoginDenied) {
			store.dispatch('notification/add', {
				type: 'warning',
				message: 'You must enter your existing password before you can save changes.',
			});
			return;
		}

		// Define errors.
		const errorMessages: Array<string> = [];

		// Validate information.
		if (!helper.validateField('email', user.value.email)) {
			errorMessages.push('Email is not valid');
		}

		const mobile = String(user.value.mobileNumber ?? '').trim();

		if (mobile.length > 0) {
			const result = validateMobile(mobile, 'GB');

			if (!result.valid || result.type !== 'MOBILE') {
				errorMessages.push('Mobile number is not valid');
			}
		}

		if (user.value.preferred2faMethod === 'sms' && mobile.length === 0) {
			errorMessages.push('Mobile number is required for SMS 2FA');
		}

		if (user.value.name.length < 6 || user.value.name.split(' ').length < 2) {
			errorMessages.push('Full name field is too short or missing a last name');
		}

		if (user.value.preferred2faMethod === 'totp' && !user.value.totpConfigured) {
			errorMessages.push('2FA using Authentication App must be setup');
		}

		// Check for errors.
		if (errorMessages.length > 0) {
			store.dispatch('notification/add', {
				type: 'warning',
				message: `There are problems with your form: ${errorMessages.join(', ')}.`,
			});
			return;
		}

		const payload = {
			name: user.value.name,
			email: user.value.email,
			mobileNumber: user.value.mobileNumber,
			preferred2faMethod: user.value.preferred2faMethod,
			isTotpAuthenticationEnabled: user.value.isTotpAuthenticationEnabled,
			totpConfigured: user.value.totpConfigured,
			current_password: user.value.current_password,
			new_password: user.value.new_password,
			new_password_confirm: user.value.new_password_confirm,
		};

		const response = await http.patch(`/api/users/${userId.value}`, payload);
		if (response.status === 204) {
			store.dispatch('notification/add', {
				type: 'success',
				message: Messages.ACCOUNT_CHANGE_SUCCESS,
			});
			emit('change', false);
			user.value.current_password = '';
			user.value.new_password = '';
			user.value.new_password_confirm = '';

			requestAccess();
		}
	};

	const requestAccess = async () => {
		if (!store.state.account.isLoggedIn) return;
		const accessResponse = await http.post('/api/access', [], { autoRefresh: 'auto' });
		if (!accessResponse.ok) return;
		store.dispatch('account/set', await accessResponse.json());
		populateUserObject();
	};

	const populateUserObject = () => {
		const account = store.state.account;
		user.value.name = account.name;
		user.value.email = account.email;
		user.value.mobileNumber = account.mobileNumber;
		user.value.preferred2faMethod = account.preferred2faMethod;
		user.value.isTotpAuthenticationEnabled = account.isTotpAuthenticationEnabled;
		user.value.totpConfigured = account.totpConfigured;
	};

	populateUserObject();
</script>

<style lang="scss" scoped>
	@use '../assets/styles/base' as base;

	.two-columns {
		display: grid;
		grid-template-columns: 1fr 0.5fr;

		.left-column {
			padding-right: 10px;
		}

		.info {
				margin-bottom: 20px;
				line-height: 1.3rem;
			}

		.row {
			margin-bottom: 20px;
			display: flex;

			.title {
				font-weight: 600;
				margin-bottom: 5px;
			}
			.text {
				line-height: 1.3rem;
			}
			.code {
				margin-top: 10px;
			}
		}
	}

	.bullet-point {
		font-family: "Inter", sans-serif;
		background-color: base.$create-color;
		color: base.$color_netcall-lightgrey;
		border-radius: 50%;
		-moz-border-radius: 50%;
        -webkit-border-radius: 50%;
		display: inline-block;
		font-weight: bold;
		line-height: 40px;
		font-size: 18px;
        margin-right: 5px;
        text-align: center;
        width: 40px;
		height: 40px;
		min-width: 40px;
		min-height: 40px;
		margin-right: 20px;
	}

	.twofa-section {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

	.success {
		color: base.$alert-success-colour;
	}

	.warning {
		color: base.$alert-warning-colour;
	}

	.notice {
		color: base.$link-colour;
	}
</style>
